import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { WricefDecisionSummary } from "@/api/wricef-decisions";
import { DECISION_BAR } from "@/components/projects/diagnostic/acts/environment/WricefCard";
import {
  type DecisionOrUndecided,
  UNDECIDED,
} from "@/components/projects/diagnostic/data/wricefDecisions";
import { makeDecisionMeta } from "@/components/projects/diagnostic/diagnosticMeta";
import { Card, CardContent } from "@/components/ui/card";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import { cn } from "@/lib/utils";

/**
 * Os totais da decisão do arquiteto no topo do WRICEF Catalog (#1500, F3).
 *
 * São os números do projeto, e não os da grade: vêm do resumo da F1b
 * (`GET .../wricef-decisions/summary`), que usa a mesma conta do card WRICEF do
 * Ato 1. Por isso não seguem os filtros da grade, e a legenda diz isso.
 *
 * O pendente com o cliente fica num bloco à parte, como esforço em aberto: as
 * horas dele ainda não entram no total do escopo (RF-01 da EF-ARCH-001).
 *
 * Fora do `SummaryHeader`, e sem `MetricCard`: os testes do resumo do catálogo
 * contam os `metric-card-value` da aba.
 */
export function WricefDecisionTotals({ summary }: { summary?: WricefDecisionSummary }) {
  const { t } = useTranslation();
  if (!summary || !summary.universe) return null;

  const meta = makeDecisionMeta(t);
  const objectsAndHours = (objects: number, hours: number) =>
    tp(t, objects, "{{n}} object · {{hours}} h", "{{n}} objects · {{hours}} h", {
      n: formatCount(objects),
      hours: formatCount(hours),
    });
  const countOf = (decision: string) => summary.by_decision[decision] ?? 0;
  const hoursOf = (decision: string) => summary.hours_by_decision[decision] ?? 0;
  const pendingMissingHours = summary.missing_hours_by_decision.client_validation ?? 0;

  return (
    <Card data-testid="decision-totals" className="shadow-none border">
      <CardContent className="pt-4 space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium">{t("Architect decisions")}</span>
            <span className="text-sm tabular-nums text-muted-foreground">
              {t("{{decided}} of {{total}} decided", {
                decided: formatCount(summary.decided),
                total: formatCount(summary.universe),
              })}
            </span>
          </div>
          <span className="text-[11px] text-muted-foreground">
            {t(
              "Project numbers, the same as the WRICEF card in the Diagnostic. They do not follow the filters below.",
            )}
          </span>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <DecisionTile
            token="in_scope"
            label={meta.in_scope.label}
            value={objectsAndHours(countOf("in_scope"), hoursOf("in_scope"))}
          />
          <DecisionTile
            token="out_of_scope"
            label={meta.out_of_scope.label}
            value={objectsAndHours(countOf("out_of_scope"), hoursOf("out_of_scope"))}
          />
          <DecisionTile
            token="covered_by_standard"
            label={meta.covered_by_standard.label}
            value={objectsAndHours(countOf("covered_by_standard"), hoursOf("covered_by_standard"))}
          />
          {/* Sem horas: o card também não soma as horas de quem não decidiu. */}
          <DecisionTile
            token={UNDECIDED}
            label={meta[UNDECIDED].label}
            value={tp(t, summary.undecided, "{{n}} object", "{{n}} objects", {
              n: formatCount(summary.undecided),
            })}
          />
          <DecisionTile
            token="client_validation"
            testId="decision-totals-open-effort"
            heading={t("Open effort")}
            label={meta.client_validation.label}
            value={objectsAndHours(countOf("client_validation"), hoursOf("client_validation"))}
            className="border-amber-200 bg-amber-50/60"
          >
            {pendingMissingHours > 0 && (
              <div className="text-[11px] text-amber-800">
                {t("{{count}} without hours", { count: pendingMissingHours })}
              </div>
            )}
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
              {t("Waiting for the client to validate. These hours are not in the scope total yet.")}
            </p>
          </DecisionTile>
        </div>

        {summary.in_scope_missing_hours > 0 && (
          <p className="text-[11px] text-amber-800">
            {t("{{count}} in-scope objects have no hours yet. They are not in the total.", {
              count: summary.in_scope_missing_hours,
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** Uma decisão: o quadrado de cor do card, o rótulo e a quantidade com as horas. */
function DecisionTile({
  token,
  label,
  value,
  heading,
  testId = `decision-totals-${token}`,
  className,
  children,
}: {
  token: DecisionOrUndecided;
  label: string;
  value: string;
  /** Um título acima do rótulo, para o bloco à parte do pendente. */
  heading?: string;
  testId?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div data-testid={testId} className={cn("rounded-md border px-3 py-2", className)}>
      {heading && (
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
          {heading}
        </div>
      )}
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span
          aria-hidden="true"
          className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", DECISION_BAR[token])}
        />
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold tabular-nums">{value}</div>
      {children}
    </div>
  );
}
